import React, { useEffect, useState } from 'react';
import {
  capitalizeName,
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
  isValidEmail,
} from '@utils/commons';
import { Conversation, ResidentDetails } from '@typings/ResidentTypes';
import { Role } from '@typings/ProfileTypes';
import WarningText from '@components/WarningText';
import { sendConversation, updateResidentStatus } from '@api/resident';
import { ApiResponse } from '@typings/ApiResponse';
import { useErrors } from '@contexts/ErrorsContext';
import NewErrorSummary from '@components/NewErrorSummary';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import getErrorMessage from '@utils/errorUtils';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';

export interface ConversationDetails {
  fullName: string;
  email?: string;
  phoneNumber?: string;
  relationship?: string;
  isAllowed?: boolean | null;
}

export interface NextOfKinDetails {
  fullName: string;
  relationship: string;
  phoneNumber: string;
}
interface HavingConversationFormProps {
  role: string;
  slug: string | undefined;
  residentDetails: ResidentDetails | undefined;
  nextOfKinDetails: NextOfKinDetails;
  setNextOfKinDetails: (info: NextOfKinDetails) => void;
  onFormSubmit: () => void;
}

const HavingConversationForm: React.FC<HavingConversationFormProps> = ({
  role,
  slug,
  residentDetails,
  nextOfKinDetails,
  setNextOfKinDetails,
  onFormSubmit,
}) => {
  const [residentFullName, setResidentFullName] = useState('');
  const { errors, setErrors } = useErrors();
  const [formInfo, setFormInfo] = useState<ConversationDetails>({
    fullName: '',
    email: '',
    phoneNumber: '',
    relationship: '',
    isAllowed: null,
  });
  const [assignInfo, setAssignInfo] = useState<{
    hasNextOfKin: boolean;
    isDisabled: boolean;
    isChecked: boolean;
  }>({
    hasNextOfKin: false,
    isDisabled: false,
    isChecked: false,
  });
  const [errMessage, setErrMessage] = useState<{
    fullName: string | undefined;
    email: string | undefined;
  }>({
    fullName: '',
    email: '',
  });

  const [addConversationStatus, setAddConversationStatus] =
    useState<SubmitStatus>({
      loading: false,
      submitted: SubmitStatusEnum.IDLE,
      message: '',
    });

  useEffect(() => {
    if (residentDetails) {
      const {
        profile: {
          full_name: fullName,
          nfk_name: nfkName,
          nfk_phone: nfkPhone,
          nfk_relationship: nfkRelationship,
        },
      } = residentDetails;
      setResidentFullName(fullName);
      setNextOfKinDetails({
        fullName: nfkName || '',
        relationship: nfkRelationship || '',
        phoneNumber: nfkPhone || '',
      });
    }
  }, [residentDetails, setNextOfKinDetails]);

  const onFinish = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'fullName',
      formInfo.fullName.trim() ? '' : 'Please enter full name'
    );

    validationErrors = updateErrors(
      validationErrors,
      'email',
      !isValidEmail(formInfo.email?.trim())
        ? 'Please enter a valid email'
        : null
    );

    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    if (slug) {
      setAddConversationStatus(prev => ({ ...prev, loading: true }));
      try {
        const response: ApiResponse<Conversation> = await sendConversation({
          ...formInfo,
          nextOfKin: assignInfo.isChecked,
          slug,
        });
        if (response && response.status === 'success') {
          setFormInfo({
            fullName: '',
            email: '',
            phoneNumber: '',
            relationship: '',
            isAllowed: null,
          });
          setAddConversationStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message: 'You have confirmed that a conversation took place',
          }));
          setTimeout(() => {
            setAddConversationStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
          await updateResidentStatus(slug, 'conversations');
          onFormSubmit();
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        setAddConversationStatus({
          loading: false,
          submitted: SubmitStatusEnum.FAILED,
          message: errorMessage,
        });
      } finally {
        setAddConversationStatus(prev => ({ ...prev, loading: false }));
        setAssignInfo(prev => ({
          ...prev,
          isChecked: false,
        }));
      }
    }
  };

  useEffect(() => {
    setErrMessage({
      fullName: getErrorMessageFromErrors(errors, 'fullName'),
      email: getErrorMessageFromErrors(errors, 'email'),
    });
  }, [errors]);

  const handleCheckboxChange = async (e: any) => {
    const { checked } = e.target;

    if (checked) {
      if (assignInfo.hasNextOfKin) {
        setFormInfo({
          ...formInfo,
          fullName: nextOfKinDetails.fullName || formInfo.fullName,
          phoneNumber: nextOfKinDetails.phoneNumber || formInfo.phoneNumber,
          relationship: nextOfKinDetails.relationship || formInfo.relationship,
        });
        setAssignInfo({ ...assignInfo, isChecked: checked });
      } else {
        setAssignInfo(prev => ({ ...prev, isChecked: true }));
        setTimeout(() => {
          setAssignInfo(prev => ({
            ...prev,
            isChecked: false,
            isDisabled: true,
          }));
        }, 500);
      }
    } else if (!checked) {
      setAssignInfo(prev => ({
        ...prev,
        isChecked: false,
      }));
      if (assignInfo.hasNextOfKin) {
        setFormInfo({
          ...formInfo,
          fullName: '',
          phoneNumber: '',
          relationship: '',
        });
      }
    }
  };

  useEffect(() => {
    setAssignInfo({
      ...assignInfo,
      hasNextOfKin:
        !!nextOfKinDetails.fullName && !!nextOfKinDetails.relationship,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOfKinDetails]);

  const handlePermissionChange = (value: boolean) => {
    setFormInfo(prevState => ({
      ...prevState,
      isAllowed: value,
    }));
  };

  return (
    <div className="mt-10 govuk-bp:mt-14">
      <h1 className="govuk-heading-l">I have had conversations with…</h1>
      {addConversationStatus.submitted === SubmitStatusEnum.SUCCESS && (
        <SuccessNotificationBanner
          title={addConversationStatus.message}
          duration={3000}
          noScrollToTop
        />
      )}
      <p className="govuk-body">
        I have had conversations about my future health and social care wishes
        with the following people.
      </p>

      <form>
        <div className="govuk-form-group">
          <fieldset
            className="govuk-fieldset"
            aria-describedby="asigned-nok-hint"
          >
            <div className="govuk-checkboxes" data-module="govuk-checkboxes">
              <div className="govuk-checkboxes__item">
                <input
                  className="govuk-checkboxes__input"
                  id="asigned-nok"
                  name="asigned-nok"
                  type="checkbox"
                  checked={assignInfo.isChecked}
                  disabled={assignInfo.isDisabled}
                  onChange={handleCheckboxChange}
                />
                <label
                  className="govuk-label govuk-checkboxes__label"
                  htmlFor="asigned-nok"
                >
                  If this person is the assigned Next of Kin for{' '}
                  {residentFullName ? capitalizeName(residentFullName) : '...'}{' '}
                  check the box.
                </label>
              </div>
            </div>
          </fieldset>
          {assignInfo.isDisabled && (
            <WarningText content="A Next of Kin has not been added to this care plan" />
          )}
        </div>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        {addConversationStatus.submitted === SubmitStatusEnum.FAILED && (
          <NewErrorSummary
            errors={[
              { id: 'addConversation', message: addConversationStatus.message },
            ]}
          />
        )}
        <div
          className={`govuk-form-group ${errMessage.fullName ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="fullName">
            Full Name
          </label>
          {errMessage.fullName && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errMessage.fullName}
            </p>
          )}
          <div className="govuk-hint" id="full-name-hint" />
          <input
            className={`govuk-input govuk-input--width-20 ${errMessage.fullName ? 'govuk-input--error' : ''}`}
            id="fullName"
            name="fullName"
            type="text"
            disabled={assignInfo.isChecked}
            aria-describedby={
              errMessage.fullName ? 'full-name-input-error' : undefined
            }
            autoComplete="fullName"
            value={formInfo.fullName}
            onChange={e =>
              setFormInfo(prevState => ({
                ...prevState,
                fullName: e.target.value,
              }))
            }
          />
        </div>
        <div
          className={`govuk-form-group ${errMessage.email ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="email">
            Email Address
          </label>
          {errMessage.email && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {errMessage.email}
            </p>
          )}
          <input
            className={`govuk-input govuk-input--width-20 ${errMessage.email ? 'govuk-input--error' : ''}`}
            id="email"
            name="email"
            type="email"
            spellCheck={false}
            aria-describedby={
              errMessage.email ? 'email-input-error' : undefined
            }
            autoComplete="email"
            value={formInfo.email}
            onChange={e =>
              setFormInfo(prevState => ({
                ...prevState,
                email: e.target.value,
              }))
            }
          />
        </div>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="phone-number">
            Phone Number
          </label>
          <input
            className="govuk-input govuk-input--width-20"
            id="phone-number"
            name="phoneNumber"
            type="tel"
            autoComplete="tel"
            aria-describedby="having-conversations-phone-number"
            maxLength={11}
            disabled={assignInfo.isChecked}
            value={formInfo.phoneNumber}
            onChange={e => {
              const { value } = e.target;
              // Check if value only contains numbers and has a maximum of 11 digits
              if (/^[0-9]*$/.test(value) && value.length <= 11) {
                setFormInfo({
                  ...formInfo,
                  phoneNumber: value,
                });
              }
            }}
          />
        </div>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="relationship-input">
            Relationship
          </label>
          <div className="govuk-hint" id="relationship-hint" />
          <input
            className="govuk-input govuk-input--width-20"
            id="relationship-input"
            name="relationship"
            type="text"
            disabled={assignInfo.isChecked}
            autoComplete="relationship"
            value={formInfo.relationship}
            onChange={e =>
              setFormInfo({ ...formInfo, relationship: e.target.value })
            }
          />
        </div>
        <div className="govuk-form-group">
          <fieldset
            className="govuk-fieldset"
            aria-describedby="changedName-hint"
          >
            <p className="govuk-body underline govuk-!-font-weight-bold">
              Permission to discuss status
            </p>
            <div id="changedName-hint" className="govuk-hint">
              Do you give us permission to discuss your Advance Care Plan,
              wishes, health and care circumstances now and in the future with
              this person?
            </div>
            <div
              className="govuk-radios govuk-radios--inline"
              data-module="govuk-radios"
            >
              <div className="govuk-radios__item">
                <input
                  className="govuk-radios__input"
                  id="is-allowed-yes"
                  name="changedName"
                  type="radio"
                  value="yes"
                  checked={formInfo.isAllowed === true}
                  onChange={() => handlePermissionChange(true)}
                />
                <label
                  className="govuk-label govuk-radios__label"
                  htmlFor="is-allowed-yes"
                >
                  Yes
                </label>
              </div>
              <div className="govuk-radios__item">
                <input
                  className="govuk-radios__input"
                  id="is-allowed-no"
                  name="changedName"
                  type="radio"
                  value="no"
                  checked={formInfo.isAllowed === false}
                  onChange={() => handlePermissionChange(false)}
                />
                <label
                  className="govuk-label govuk-radios__label"
                  htmlFor="is-allowed-no"
                >
                  No
                </label>
              </div>
            </div>
          </fieldset>
        </div>

        <button
          type="submit"
          disabled={
            role === Role.TeamMemberViewOnly || addConversationStatus.loading
          }
          className="govuk-button govuk-!-margin-0"
          onClick={onFinish}
        >
          Save
        </button>
      </form>
    </div>
  );
};

export default HavingConversationForm;
